import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Package, ArrowLeft, Download, AlertCircle, Loader2, CheckCircle2, Star, ChevronRight } from 'lucide-react';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import gsap from 'gsap';
import toast from 'react-hot-toast';
import { fetchMyOrders } from '../redux/slice/order.slice';
import { fetchUserLibrary } from '../redux/slice/user.slice';
import { submitReview, fetchUserReviews, updateReview } from '../redux/slice/review.slice';
import { fetchDownloadLink } from '../redux/slice/game.slice';
import { getGamePath } from '../utils/gamePath';
import LazyImage from '../components/common/LazyImage';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import libraryAstronautImg from '../assets/images/library.webp';
import { useGridCorners } from '../hooks/useGridCorners';

const ReviewSchema = Yup.object().shape({
    rating: Yup.number().min(1, 'Rating must be at least 1 star').max(5).required('Rating is required'),
    comment: Yup.string().required('Review comment is required').min(5, 'Review must be at least 5 characters long')
});

const LibraryCardSkeleton = ({ style = {} }) => (
    <div
        style={style}
        className="border border-white/10 bg-[#080814]/85 backdrop-blur-xl p-4 sm:p-5 space-y-3 animate-pulse overflow-hidden"
    >
        <div className="aspect-[16/10] bg-white/[0.04] relative overflow-hidden" style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
            <div className="absolute top-3 right-3 h-5 w-16 bg-white/10 rounded-lg"></div>
        </div>
        <div className="flex justify-between items-center pt-1">
            <div className="h-4 w-16 bg-cyber-purple/20 rounded-md"></div>
        </div>
        <div className="h-4 w-3/4 bg-white/10 rounded"></div>
        <div className="h-3 w-1/2 bg-white/5 rounded"></div>
        <div className="pt-3 border-t border-white/10 flex flex-col gap-2">
            <div className="h-2.5 w-24 bg-white/10 rounded"></div>
            <div className="flex gap-2 pt-1">
                <div className="h-7 w-24 bg-white/10 rounded-lg"></div>
                <div className="h-7 w-20 bg-cyber-purple/20 rounded-lg"></div>
            </div>
        </div>
    </div>
);

export default function Library() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((s) => s.auth);
    const { myOrders: orders, loading: ordersLoading, error } = useSelector((s) => s.order);
    const { userReviews } = useSelector((s) => s.review);

    const [apiLibrary, setApiLibrary] = useState([]);
    const [libraryLoading, setLibraryLoading] = useState(true);
    const [selectedGame, setSelectedGame] = useState(null);
    const [openRateModal, setOpenRateModal] = useState(false);
    const [hoverRating, setHoverRating] = useState(0);
    const containerRef = useRef(null);

    const { gridCols, setGridRef, getCornerStyle } = useGridCorners();

    const loading = libraryLoading || ordersLoading;

    const handleReviewSubmit = async (values, { setSubmitting, resetForm }) => {
        if (!selectedGame) return;
        const gameId = selectedGame._id || selectedGame.id;
        const existingReview = userReviews?.find(rev => (rev.game?._id || rev.game) === gameId);
        try {
            if (existingReview) {
                await dispatch(updateReview({
                    reviewId: existingReview._id || existingReview.id,
                    rating: values.rating,
                    comment: values.comment
                })).unwrap();
                toast.success(`Your review for ${selectedGame.title} has been updated and submitted for approval.`, {
                    style: {
                        background: '#070710',
                        color: '#00f0ff',
                        border: '1px solid rgba(0, 240, 255, 0.2)',
                        fontSize: '11px',
                    }
                });
            } else {
                await dispatch(submitReview({ gameId, rating: values.rating, comment: values.comment })).unwrap();
                toast.success(`Thank you for rating ${selectedGame.title}! Your review has been submitted for approval.`, {
                    style: {
                        background: '#070710',
                        color: '#00f0ff',
                        border: '1px solid rgba(0, 240, 255, 0.2)',
                        fontSize: '11px',
                    }
                });
            }
            setOpenRateModal(false);
            resetForm();
        } catch (err) {
            toast.error(err || "Failed to submit review");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            if (!user) {
                setLibraryLoading(false);
                return;
            }
            setLibraryLoading(true);
            try {
                const userId = user._id || user.id;
                const [libraryGames] = await Promise.all([
                    dispatch(fetchUserLibrary(userId)).unwrap(),
                    dispatch(fetchMyOrders()),
                    dispatch(fetchUserReviews(userId)),
                ]);
                setApiLibrary(libraryGames || []);
            } catch (err) {
                console.error('Library load error:', err);
                setApiLibrary([]);
            } finally {
                setLibraryLoading(false);
            }
        };
        load();
    }, [dispatch, user]);

    const libraryGames = useMemo(() => {
        const platformMap = {};

        (orders || []).forEach((order) => {
            if (order.status === 'Cancelled') return;
            (order.items || []).forEach((item) => {
                const game = item.game;
                if (!game) return;
                const id = (game._id || game.id)?.toString();
                if (!id) return;
                if (!platformMap[id]) platformMap[id] = new Set();
                platformMap[id].add(item.platform || 'windows');
            });
        });

        const source = apiLibrary.length > 0
            ? apiLibrary
            : (() => {
                const gamesMap = {};
                (orders || []).forEach((order) => {
                    if (order.status === 'Cancelled') return;
                    (order.items || []).forEach((item) => {
                        const game = item.game;
                        if (!game) return;
                        const id = game._id || game.id;
                        if (id && !gamesMap[id]) {
                            gamesMap[id] = { ...game, purchasedPlatforms: [] };
                        }
                        if (id) {
                            const plat = item.platform || 'windows';
                            if (!gamesMap[id].purchasedPlatforms.includes(plat)) {
                                gamesMap[id].purchasedPlatforms.push(plat);
                            }
                        }
                    });
                });
                return Object.values(gamesMap);
            })();

        return source.map((game) => {
            const id = (game._id || game.id)?.toString();
            const fromOrders = platformMap[id] ? [...platformMap[id]] : [];
            const existing = game.purchasedPlatforms || [];
            const merged = [...new Set([...existing, ...fromOrders])];
            return {
                ...game,
                purchasedPlatforms: merged.length > 0 ? merged : ['windows'],
            };
        });
    }, [apiLibrary, orders]);

    useEffect(() => {
        if (openRateModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [openRateModal]);

    useEffect(() => {
        if (!loading && containerRef.current) {
            gsap.fromTo(containerRef.current,
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
            );
        }
    }, [loading]);

    const handleDownload = async (game, platformKey) => {
        const platform = platformKey || 'windows';
        const gameId = game._id || game.id;
        try {
            const data = await dispatch(fetchDownloadLink({ gameId, platform })).unwrap();
            if (data && data.downloadUrl) {
                window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
                toast.success(`Starting download of ${game.title} for ${platform.toUpperCase()}...`, {
                    style: {
                        background: '#070710',
                        color: '#00f0ff',
                        border: '1px solid rgba(0, 240, 255, 0.2)',
                        fontSize: '11px',
                    }
                });
            } else {
                toast.error(`No download link available for "${game.title}" on ${platform.toUpperCase()} yet.`, {
                    style: {
                        background: '#070710',
                        color: '#ff007a',
                        border: '1px solid rgba(255, 0, 122, 0.2)',
                        fontSize: '11px',
                    }
                });
            }
        } catch (err) {
            console.error('Download link error:', err);
            toast.error(err.response?.data?.message || `Failed to fetch secure download link for ${game.title}.`, {
                style: {
                    background: '#070710',
                    color: '#ff007a',
                    border: '1px solid rgba(255, 0, 122, 0.2)',
                    fontSize: '11px',
                }
            });
        }
    };

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-cyber-pink mx-auto mb-4" />
                    <p className="text-gray-400 text-sm mb-4">Failed to load your library.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="text-cyber-blue text-xs font-cyber uppercase tracking-widest hover:underline"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-transparent text-gray-100 pt-24 pb-12 sm:pt-32 sm:pb-32 relative overflow-hidden flex flex-col items-center">
            {/* Dynamic Ambient Platform Lighting with smooth feathered fade */}
            <div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                style={{
                    maskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)'
                }}
            >
                <div className="absolute top-0 left-0 w-full h-full bg-cyber-grid opacity-[0.04]" />
                <div
                    className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] opacity-20"
                    style={{ backgroundColor: '#00f0ff' }}
                />
                <div className="absolute top-[30%] right-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-purple/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-[10%] left-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-blue/10 rounded-full blur-[150px]" />
            </div>

            <div ref={containerRef} className="container w-full relative z-10 mx-auto">
                {/* Page Header */}
                <div className="mb-4 sm:mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 relative z-50">
                    <div className="flex-shrink-0">
                        <button
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center justify-center gap-2 mb-4 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-[#151a26] border border-white/[0.08] text-xs font-cyber font-bold text-gray-200 hover:text-white hover:border-white/20 hover:bg-[#1a2130] transition-all duration-300 uppercase tracking-wider group/btn shadow-sm"
                        >
                            <ArrowLeft className="w-3.5 h-3.5 group-hover/btn:-translate-x-1 transition-transform" />
                            <span>BACK</span>
                        </button>
                        <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                            Your Collection
                        </p>
                        <h1 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-nowrap">
                            MY <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">LIBRARY</span>
                        </h1>
                        <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full"></div>
                    </div>
                </div>

                {loading ? (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div className="h-3.5 w-40 bg-white/10 rounded animate-pulse"></div>
                        </div>
                        <div
                            ref={setGridRef}
                            className="grid grid-cols-1 md600:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full"
                        >
                            {[...Array(8)].map((_, i) => (
                                <LibraryCardSkeleton
                                    key={i}
                                    style={getCornerStyle(i, 8, gridCols)}
                                />
                            ))}
                        </div>
                    </div>
                ) : libraryGames.length === 0 ? (
                    /* Premium Empty State with Astronaut Illustration UI */
                    <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center max-w-lg mx-auto space-y-4 px-4">
                        <div className="relative group w-full flex items-center justify-center p-1 sm:p-2">
                            <img
                                src={libraryAstronautImg}
                                alt="Library Empty Astronaut"
                                className="w-36 xs:w-44 sm:w-52 md:w-56 max-w-[240px] max-h-[250px] h-auto object-contain animate-astronaut-float cursor-pointer transition-transform duration-500 group-hover:scale-105"
                            />
                        </div>

                        <div className="space-y-2.5 max-w-md">
                            <h2 className="font-cyber font-black text-2xl md600:text-4xl text-[#00f0ff] uppercase tracking-wider drop-shadow-[0_0_18px_rgba(0,240,255,0.4)]">
                                LIBRARY EMPTY
                            </h2>
                            <p className="text-xs md600:text-sm text-gray-300 font-sans leading-relaxed">
                                You have not acquired any games yet. Browse our store to license your favorite titles.
                            </p>
                        </div>

                        <button
                            onClick={() => navigate('/all-games')}
                            className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap group before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                        >
                            <span>DISCOVER GAMES</span>
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <span className="text-[11px] font-cyber font-semibold text-gray-500 uppercase tracking-widest">
                                {libraryGames.length} {libraryGames.length === 1 ? 'game' : 'games'} in your library
                            </span>
                        </div>
                        <div
                            ref={setGridRef}
                            className="grid grid-cols-1 md600:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full"
                        >
                            {libraryGames.map((game, index) => {
                                const gameId = game._id || game.id;
                                const platforms = game.purchasedPlatforms || ['windows'];
                                const PlatformIcon = {
                                    windows: FaWindows,
                                    playstation: SiPlaystation,
                                    xbox: FaXbox,
                                    nintendo: BsNintendoSwitch,
                                    quest: SiOculus,
                                    android: DiAndroid,
                                    ios: FaApple,
                                    visionpro: TbDeviceVisionProFilled,
                                    steamdeck: SiSteamdeck
                                };
                                const PlatformColor = {
                                    windows: '#0078d7',
                                    playstation: '#00439c',
                                    xbox: '#107c10',
                                    nintendo: '#e60012',
                                    quest: '#ffffff',
                                    android: '#3ddc84',
                                    ios: '#a3aaae',
                                    visionpro: '#a855f7',
                                    steamdeck: '#38bdf8'
                                };
                                const labelMap = {
                                    windows: 'PC',
                                    playstation: 'PlayStation',
                                    xbox: 'Xbox',
                                    nintendo: 'Nintendo',
                                    quest: 'Quest',
                                    android: 'Android',
                                    ios: 'iOS',
                                    visionpro: 'Vision Pro',
                                    steamdeck: 'Steam Deck'
                                };

                                return (
                                    <div
                                        key={gameId}
                                        style={getCornerStyle(index, libraryGames.length, gridCols)}
                                        className="group relative flex flex-col h-full bg-[#080814]/85 backdrop-blur-xl border border-white/15 hover:border-cyber-cyan/50 cyber-card-glow transition-all duration-300 overflow-hidden"
                                    >
                                        <Link
                                            to={getGamePath(game)}
                                            className="block relative aspect-[16/10] overflow-hidden cursor-pointer"
                                            style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}
                                        >
                                            <LazyImage
                                                src={game.cover_image?.url || game.cover_image || game.images?.[0]}
                                                placeholder={game.cover_image?.lowUrl || game.cover_image || game.images?.[0]}
                                                alt={game.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />

                                            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-black/65 backdrop-blur-md border border-white/15 flex items-center gap-1.5 text-[10px] font-cyber font-bold text-cyber-green uppercase tracking-wider shadow-md">
                                                <CheckCircle2 className="w-3.5 h-3.5 text-cyber-green drop-shadow-[0_0_6px_rgba(57,255,20,0.6)]" />
                                                <span>OWNED</span>
                                            </div>
                                        </Link>

                                        <div className="p-4 sm:p-5 flex flex-col flex-1 gap-2">
                                            <div className="flex items-center justify-between gap-2 min-h-[22px]">
                                                {game.category?.name ? (
                                                    <span className="text-[9px] font-cyber font-bold uppercase tracking-widest text-cyber-purple bg-cyber-purple/15 px-2.5 py-0.5 rounded-md border border-cyber-purple/30 group-hover:border-cyber-purple/60 group-hover:shadow-[0_0_10px_rgba(189,0,255,0.3)] transition-all truncate">
                                                        {game.category.name}
                                                    </span>
                                                ) : (
                                                    <span className="text-[9px] font-cyber font-bold uppercase tracking-widest text-cyber-purple/60 bg-cyber-purple/10 px-2.5 py-0.5 rounded-md border border-cyber-purple/20 truncate">
                                                        GAME
                                                    </span>
                                                )}
                                            </div>

                                            <Link to={getGamePath(game)}>
                                                <h3 className="font-cyber font-bold text-sm text-white group-hover:text-cyber-cyan transition-colors duration-300 line-clamp-1 uppercase tracking-tight">
                                                    {game.title}
                                                </h3>
                                            </Link>

                                            {game.tagline ? (
                                                <p className="text-gray-400 text-[11px] line-clamp-1 leading-relaxed font-sans h-4">
                                                    {game.tagline}
                                                </p>
                                            ) : (
                                                <p className="text-[11px] line-clamp-1 leading-relaxed font-sans h-4 invisible">
                                                    &nbsp;
                                                </p>
                                            )}

                                            <div className="pt-3 mt-1 border-t border-white/10 flex flex-col gap-2 flex-1 justify-between">
                                                <span className="text-[9px] font-cyber font-semibold text-gray-500 uppercase tracking-wider">
                                                    DOWNLOAD PLATFORMS:
                                                </span>
                                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                                    {platforms.map((plat) => {
                                                        const Icon = PlatformIcon[plat] || FaWindows;
                                                        return (
                                                            <button
                                                                key={plat}
                                                                onClick={() => handleDownload(game, plat)}
                                                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-cyber font-bold uppercase tracking-wider border bg-white/5 border-white/10 text-gray-300 hover:border-cyber-cyan/50 hover:text-cyber-cyan hover:bg-cyber-cyan/10 transition-all duration-300 cursor-pointer"
                                                            >
                                                                <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: PlatformColor[plat] || '#9ca3af' }} />
                                                                <span className="truncate">{labelMap[plat] || plat}</span>
                                                                <Download className="w-3 h-3 ml-0.5 shrink-0" />
                                                            </button>
                                                        );
                                                    })}

                                                    {(() => {
                                                        const review = userReviews?.find(rev => (rev.game?._id || rev.game) === gameId);
                                                        if (review) {
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSelectedGame(game);
                                                                        setOpenRateModal(true);
                                                                    }}
                                                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-cyber font-bold uppercase tracking-wider border bg-cyber-cyan/10 border-cyber-cyan/40 text-cyber-cyan hover:bg-cyber-cyan/20 hover:border-cyber-cyan/60 hover:shadow-[0_0_12px_rgba(0,240,255,0.3)] transition-all cursor-pointer"
                                                                >
                                                                    <Star className="w-3.5 h-3.5 fill-cyber-cyan/20 text-cyber-cyan shrink-0" />
                                                                    <span>Edit Review</span>
                                                                </button>
                                                            );
                                                        } else {
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSelectedGame(game);
                                                                        setOpenRateModal(true);
                                                                    }}
                                                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-cyber font-bold uppercase tracking-wider border bg-cyber-purple/10 border-cyber-purple/40 text-cyber-purple hover:bg-cyber-purple/20 hover:border-cyber-purple/60 hover:shadow-[0_0_12px_rgba(189,0,255,0.3)] transition-all cursor-pointer"
                                                                >
                                                                    <Star className="w-3.5 h-3.5 text-cyber-purple shrink-0" />
                                                                    <span>Rate Game</span>
                                                                </button>
                                                            );
                                                        }
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Rate Game Modal */}
            {openRateModal && selectedGame && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="relative w-full max-w-md rounded-2xl p-6 max-h-[90vh] overflow-y-auto border border-cyber-purple/30"
                        style={{ background: 'linear-gradient(135deg, #0e0e1c 0%, #05050c 100%)', boxShadow: '0 0 40px rgba(189, 0, 255, 0.2)' }}>
                        <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-cyber-purple to-cyber-pink"></div>
                        <h3 className="text-sm font-cyber font-medium text-cyber-purple uppercase tracking-widest mb-2 flex items-center gap-2">
                            <Star className="w-5 h-5" /> Rate & Review Game
                        </h3>
                        <p className="text-xs text-gray-400 font-sans mb-4">
                            Share your feedback for <span className="text-white font-semibold">{selectedGame.title}</span>
                        </p>

                        <Formik
                            initialValues={{
                                rating: (() => {
                                    const rev = userReviews?.find(r => (r.game?._id || r.game) === (selectedGame._id || selectedGame.id));
                                    return rev ? rev.rating : 5;
                                })(),
                                comment: (() => {
                                    const rev = userReviews?.find(r => (r.game?._id || r.game) === (selectedGame._id || selectedGame.id));
                                    return rev ? rev.comment : '';
                                })()
                            }}
                            validationSchema={ReviewSchema}
                            onSubmit={handleReviewSubmit}
                            enableReinitialize
                        >
                            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                                <Form className="space-y-4">
                                    <div className="flex flex-col items-center gap-2 py-4 bg-white/[0.01] border border-white/5 rounded-xl">
                                        <span className="text-[10px] font-cyber text-gray-500 uppercase tracking-wider">Select Rating</span>
                                        <div className="flex items-center gap-1.5">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    key={star}
                                                    type="button"
                                                    onClick={() => setFieldValue('rating', star)}
                                                    onMouseEnter={() => setHoverRating(star)}
                                                    onMouseLeave={() => setHoverRating(0)}
                                                    className="p-1 transition-transform hover:scale-125 focus:outline-none cursor-pointer"
                                                >
                                                    <Star
                                                        className={`w-8 h-8 transition-colors ${star <= (hoverRating || values.rating)
                                                            ? 'text-amber-400 fill-amber-400 filter drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]'
                                                            : 'text-gray-700'
                                                            }`}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                        <span className="text-xs font-cyber font-bold text-amber-400 mt-1">
                                            {hoverRating || values.rating} / 5 ({
                                                (hoverRating || values.rating) === 5 ? 'Masterpiece' :
                                                    (hoverRating || values.rating) === 4 ? 'Great' :
                                                        (hoverRating || values.rating) === 3 ? 'Good' :
                                                            (hoverRating || values.rating) === 2 ? 'Mediocre' : 'Awful'
                                            })
                                        </span>
                                        {errors.rating && touched.rating && (
                                            <p className="text-[10px] text-red-500 mt-1">{errors.rating}</p>
                                        )}
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Review Comments</label>
                                        <Field
                                            as="textarea"
                                            name="comment"
                                            rows={4}
                                            placeholder="Write your constructive thoughts about this game..."
                                            className={`w-full px-4 py-3 rounded-xl text-xs md:text-sm font-cyber text-white bg-white/[0.04] border border-white/10 focus:border-cyber-purple/50 focus:outline-none transition-all placeholder:text-gray-700 resize-none ${errors.comment && touched.comment ? 'border-red-500/50 focus:border-red-500/50' : ''
                                                }`}
                                        />
                                        {errors.comment && touched.comment && (
                                            <p className="text-[10px] text-red-500">{errors.comment}</p>
                                        )}
                                    </div>

                                    <div className="flex justify-end gap-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={() => setOpenRateModal(false)}
                                            className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 text-xs font-cyber transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-6 py-2.5 rounded-xl bg-cyber-purple hover:bg-cyber-purple/80 text-white text-xs font-cyber font-bold transition-all shadow-[0_0_15px_rgba(189,0,255,0.3)] cursor-pointer disabled:opacity-50"
                                        >
                                            {isSubmitting ? 'Submitting...' : 'Submit Review'}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            )}
        </div>
    );
}
